import React, { Suspense, lazy, useEffect, useState, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { supabase } from './lib/supabase';
import { SEO, SEOConfigs } from './components/SEO';
import { HelmetProvider } from 'react-helmet-async';

// Lazy load components for better performance
const Login = lazy(() => import('@/pages/Login'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('@/pages/PaymentCancel'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout').then(module => ({ default: module.AdminLayout })));
const SessionExpired = lazy(() => import('@/pages/SessionExpired'));
const NotificationProvider = lazy(() => import('@/components/NotificationSystem').then(module => ({ default: module.NotificationProvider })));
const Header = lazy(() => import('@/components/Header'));
const Hero = lazy(() => import('@/components/landing/Hero'));
const Problem = lazy(() => import('@/components/landing/Problem'));
const Solution = lazy(() => import('@/components/landing/Solution'));
const HowItWorks = lazy(() => import('@/components/landing/HowItWorks'));
const Testimonials = lazy(() => import('@/components/landing/Testimonials'));
const Features = lazy(() => import('@/components/landing/Features'));
const Pricing = lazy(() => import('@/components/landing/Pricing'));
const Security = lazy(() => import('@/components/landing/Security'));
const FAQ = lazy(() => import('@/components/landing/FAQ'));
const Footer = lazy(() => import('@/components/landing/Footer'));

// Loading component
const LoadingSpinner = ({ message = "Carregando..." }: { message?: string }) => (
    <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">{message}</p>
        </div>
    </div>
);

const LandingPage = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    console.log('[LandingPage] Render - user:', !!user, 'loading:', loading);

    useEffect(() => {
        console.log('[LandingPage] useEffect - user:', !!user, 'loading:', loading);
        if (!loading && user) {
            console.log('[LandingPage] Redirecting to dashboard');
            navigate('/dashboard');
        }
    }, [user, loading, navigate]);

    if (loading) {
        console.log('[LandingPage] Showing loading spinner');
        return <LoadingSpinner />;
    }

    console.log('[LandingPage] Rendering landing page content');
    return (
        <>
            <SEO {...SEOConfigs.home} />
            <div className="min-h-screen bg-white">
                <Header />
                <main>
                    <Hero />
                    <Problem />
                    <Solution />
                    <HowItWorks />
                    <Testimonials />
                    <Features />
                    <Pricing />
                    <Security />
                    <FAQ />
                </main>
                <Footer />
            </div>
        </>
    );
};



const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, loading } = useAuth();
    const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
    const retryCountRef = useRef(0);
    const navigate = useNavigate();

    console.log('[ProtectedRoute] Render - user:', !!user, 'loading:', loading, 'onboardingCompleted:', onboardingCompleted);

    useEffect(() => {
        console.log('[ProtectedRoute] useEffect triggered - user:', !!user, 'loading:', loading);
        if (!user) {
            console.log('[ProtectedRoute] No user, resetting state');
            setOnboardingCompleted(null);
            retryCountRef.current = 0;
            return;
        }

        // Verificar onboarding apenas uma vez por sessão
        const checkOnboarding = async () => {
            try {
                console.log('[ProtectedRoute] Iniciando verificação de onboarding para user:', user.id);

                const { data, error } = await supabase
                    .from('profiles')
                    .select('onboarding_completed')
                    .eq('id', user.id)
                    .single();

                console.log('[ProtectedRoute] Query result - data:', data, 'error:', error);

                if (error) {
                    console.warn('[ProtectedRoute] Erro ao buscar onboarding:', error);

                    // Se erro é "not found" (PGRST116), significa que perfil não existe
                    if (error.code === 'PGRST116') {
                        console.log('[ProtectedRoute] Perfil não encontrado - assumindo novo usuário');
                        setOnboardingCompleted(false);
                        if (window.location.pathname !== '/onboarding') {
                            console.log('[ProtectedRoute] Redirecionando para onboarding');
                            navigate('/onboarding');
                        }
                        return;
                    }

                    // Para outros erros, tentar uma abordagem mais simples
                    console.warn('[ProtectedRoute] Outro erro, assumindo novo usuário');
                    setOnboardingCompleted(false);
                    if (window.location.pathname !== '/onboarding') {
                        navigate('/onboarding');
                    }
                    return;
                }

                const completed = data?.onboarding_completed || false;
                console.log('[ProtectedRoute] Onboarding status:', completed);
                setOnboardingCompleted(completed);
                retryCountRef.current = 0; // Reset retry count on success

                // Lógica de navegação baseada no status
                if (!completed && window.location.pathname !== '/onboarding') {
                    console.log('[ProtectedRoute] Redirecionando para onboarding (não completado)');
                    navigate('/onboarding');
                } else if (completed && window.location.pathname === '/onboarding') {
                    console.log('[ProtectedRoute] Redirecionando para dashboard (completado)');
                    navigate('/dashboard');
                }

            } catch (error) {
                console.error('[ProtectedRoute] Erro inesperado:', error);

                // Em caso de erro, assumir novo usuário para não travar
                console.warn('[ProtectedRoute] Erro inesperado, assumindo novo usuário');
                setOnboardingCompleted(false);
                if (window.location.pathname !== '/onboarding') {
                    navigate('/onboarding');
                }
            }
        };

        checkOnboarding();
    }, [user?.id, navigate]); // Dependências mínimas para evitar re-execuções

    // Timeout safety for onboarding check
    useEffect(() => {
        if (!loading && onboardingCompleted === null && user) {
            const timer = setTimeout(() => {
                console.warn('[ProtectedRoute] Timeout de verificação atingido (30s)');
                setOnboardingCompleted(true);
            }, 30000);
            return () => clearTimeout(timer);
        }
    }, [loading, onboardingCompleted, user]);

    // Loading states
    if (loading || onboardingCompleted === null) {
        return <LoadingSpinner message="Verificando perfil..." />;
    }

    // Redirecionamentos
    if (!user) {
        console.log('[ProtectedRoute] No user, redirecting to login');
        return <Navigate to="/login" replace />;
    }

    if (onboardingCompleted === false && window.location.pathname !== '/onboarding') {
        console.log('[ProtectedRoute] Onboarding not completed, redirecting to onboarding');
        return <Navigate to="/onboarding" replace />;
    }

    if (onboardingCompleted === true && window.location.pathname === '/onboarding') {
        console.log('[ProtectedRoute] Onboarding completed, redirecting to dashboard');
        return <Navigate to="/dashboard" replace />;
    }

    console.log('[ProtectedRoute] Rendering children');
    return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, loading } = useAuth();
    const [profileRole, setProfileRole] = useState<string | null>(null);
    const [checkingRole, setCheckingRole] = useState(true);

    useEffect(() => {
        if (!user) {
            setCheckingRole(false);
            return;
        }

        // Buscar role do perfil no banco
        const checkAdminRole = async () => {
            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('role')
                    .eq('id', user.id)
                    .single();

                if (!error && data) {
                    const role = data.role || 'user';
                    console.log('[AdminRoute] Role encontrado:', role);
                    setProfileRole(role);
                } else {
                    console.warn('[AdminRoute] Erro ao buscar role:', error);
                    setProfileRole('user');
                }
            } catch (err) {
                console.error('[AdminRoute] Erro ao verificar admin role:', err);
                setProfileRole('user');
            } finally {
                setCheckingRole(false);
            }
        };

        checkAdminRole();
    }, [user]);

    if (loading || checkingRole) {
        return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Verificar se é admin (por email OU por role no banco)
    const isAdminByEmail = user.email === import.meta.env.VITE_ADMIN_EMAIL;
    const isAdminByRole = profileRole === 'admin';
    const isAdmin = isAdminByEmail || isAdminByRole;

    console.log('[AdminRoute] Verificação admin:', {
        email: user.email,
        isAdminByEmail,
        profileRole,
        isAdminByRole,
        isAdmin
    });

    if (!isAdmin) {
        console.warn('[AdminRoute] Acesso negado - redirecionando para dashboard');
        return <Navigate to="/dashboard" replace />;
    }

    console.log('[AdminRoute] Acesso permitido - renderizando painel admin');

    return <>{children}</>;
};

export const AppRoutes = () => {
    const navigate = useNavigate();
    const { signOut } = useAuth();

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    const handleAdminLogout = () => {
        // Apenas navega de volta ao dashboard sem fazer logout
        navigate('/dashboard');
    };

    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={
                <>
                    <SEO {...SEOConfigs.login} />
                    <Login />
                </>
            } />
            <Route path="/session-expired" element={
                <>
                    <SEO title="Sessão Expirada | Driver Finanças" description="Sua sessão expirou por segurança." />
                    <SessionExpired />
                </>
            } />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/onboarding" element={
                <ProtectedRoute>
                    <>
                        <SEO {...SEOConfigs.onboarding} />
                        <Onboarding onComplete={() => navigate('/dashboard')} />
                    </>
                </ProtectedRoute>
            } />

            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <>
                        <SEO {...SEOConfigs.dashboard} />
                        <NotificationProvider>
                            <Dashboard onLogout={handleLogout} />
                        </NotificationProvider>
                    </>
                </ProtectedRoute>
            } />

            {/* Payment Routes */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route path="/admin/*" element={
                <AdminRoute>
                    <AdminLayout onLogout={handleAdminLogout} />
                </AdminRoute>
            } />
        </Routes>
    );
};
